import { useEffect, useRef, cloneElement, ReactElement } from 'react';

interface ObservableProps {
  isVisible: () => void;
  isLast: boolean;
  focus?: boolean;
}

interface ObservableWrapperProps extends ObservableProps {
  children: ReactElement;
}

// Check if the last item in the list is being viewed
const ObservableWrapper = ({ children, isVisible, isLast, focus = false }: ObservableWrapperProps) => {
  const childRef = useRef<HTMLElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  /**
   * Implement Intersection Observer to check if the last Component in an array is visible on the screen, then call isVisible() function
   */
  useEffect(() => {
    if (!childRef?.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (isLast && entry.isIntersecting) {
        isVisible();
        observer.unobserve(entry.target);
      }
    });
    if (observerRef?.current) {
      observerRef.current.unobserve(childRef.current);
    }
    observerRef.current = observer;
    observerRef.current.observe(childRef.current);
  }, [isLast, isVisible]);

  useEffect(() => {
    if (focus && childRef?.current) {
      const focusableElement =
        childRef.current.tagName.toLowerCase() === 'a' ? childRef.current : childRef.current.querySelector('a');
      if (focusableElement) focusableElement.focus();
    }
  }, [focus]);

  // @ts-expect-error TS(2322)
  return cloneElement(children, { ref: childRef });
};

export default ObservableWrapper;
